import React, {useMemo} from 'react'
import { Button } from 'antd'
import moment from 'moment'
import { IPlotVariable } from '../../../../../../types/analytics'
import { IBestDataset } from '../../../../../../types/analytics'
import { IEnrichedMetadata } from '../../../../../../types/metadata'
import {
  IGetBasisChartV2ApiPayload,
  IGetJointScatterPlotV2ApiPayload,
} from '../../../../../../types/chart'
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput'
import {useScatterPlotParams} from "../../../hooks/useScatterPlotParams";
import {ScatterPlotInputForm} from "./ScatterPlotInputForm";
import {ScatterPlotChart} from "./ScatterPlotChart";
import {ScatterPlotAdditionalInput} from "./ScatterPlotAdditionalInput";
import {AnalyticsSection} from "../../Layout/AnalyticsSection";

interface ScatterPlotProps {
  coordinates: number[]
  previewDatasetId: string
  previewDatasetTimeRange: [moment.Moment, moment.Moment]
  categoryDataset: IBestDataset[]
  onDatasetChange: (item: string) => void
  defaultValueShortName?: string
  defaultDirectionShortName?: string
  enrichedMetadata: IEnrichedMetadata[]
  variables: IPlotVariable[]
  dateRange: string[]
  tsData: { [key: string]: any }[]
  onUpdateScatterPlot: (values: IGetBasisChartV2ApiPayload) => void
  onUpdateJointScatterPlot: (values: IGetJointScatterPlotV2ApiPayload) => void
  updatedScatterPlotData: any[]
  pointProps: string
  pointName: string
  userInput: IAnalytcisUserInput
  gridCellSize: string
  waterDepth: string
  updating: boolean
  error: string
  previewError: string
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
  isOffline: boolean
}

export default function ScatterPlot(props: ScatterPlotProps) {
  const {
    updating,
    error,
    previewError,
    pointName,
    isOffline
  } = props

  const {
    datasetY,
    variableX,
    variableY,
    variablesX,
    variablesY,
    seriesX,
    seriesY,
    plotData,
    startDate,
    endDate,
    downloadName,
    categories,
    memoCoords,
    dataPreformanceWarning,
    selectedDatasetInfo,
    doStartDateChange,
    doEndDateChange,
    doDatasetXChange,
    doDatasetYChange,
    doVariableChange,
    handleCheckboxChange,
    onUpdateScatterPlotData,
    disabledStartDate,
    disabledEndDate,
    xAxisInterval,
    yAxisInterval,
    month,
    doXAxisIntervalChange,
    doYAxisIntervalChange,
    doMonthChange
  } = useScatterPlotParams(props)

  // TODO: enable cache 
  // const upsertScatterCache = useAnalyticsCacheStore(state => state.upsertScatterCache)

  // useEffect(() => {
  //   if (!props.coordinates || !props.previewDatasetId || !variableX?.Index) return;

  //   const key = `${props.previewDatasetId}_${variableX?.Index}_${props.coordinates[1]},${props.coordinates[0]}`;
  //   upsertScatterCache(key, {
  //     variableXIndex: variableX?.Index,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     seriesX,
  //     seriesY,
  //     plotData,
  //     xAxisInterval,
  //     yAxisInterval,
  //     downloadName,
  //     selectedDatasetInfo
  //   });
  // }, [upsertScatterCache, props.coordinates, props.previewDatasetId, variableX?.Index, startDate, endDate, seriesX, seriesY, plotData, xAxisInterval, yAxisInterval, downloadName]);

  const plotInputContent = useMemo(() => (
    <ScatterPlotInputForm
      categories={categories}
      categoryDataset={props.categoryDataset}
      variablesX={variablesX}
      datasetX={props.previewDatasetId}
      variableX={variableX}
      onDatasetXChange={doDatasetXChange}
      onVariableChange={doVariableChange}
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={doStartDateChange}
      onEndDateChange={doEndDateChange}
      disabledStartDate={disabledStartDate}
      disabledEndDate={disabledEndDate}
      isForAllAnalytics={props.isForAllAnalytics}
      onCheckboxChange={handleCheckboxChange}
      isLoading={updating}
      hasError={!!previewError}
    />
  ), [
    props.previewDatasetId,
    props.onDatasetChange,
    categories,
    variablesX,
    variableX,
    doDatasetXChange,
    doVariableChange,
    startDate,
    endDate,
    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    props.isForAllAnalytics,
    handleCheckboxChange,
    updating,
    previewError
  ])

  const updateContent = useMemo(() => (
    <div style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '36px'
    }}>
      <Button
        type="primary"
        id="Analytics-ScatterPlot-Update_Button"
        disabled={!!previewError}
        loading={updating}
        onClick={() => onUpdateScatterPlotData()}
        style={{
          width: '90px',
          height: '36px'
        }}
      >
        Update
      </Button>
    </div>
  ), [
    previewError,
    updating,
    onUpdateScatterPlotData
  ])

  const emptyText = useMemo(() => {
    if (props.previewDatasetId.includes('CMEMS')) {
      return 'Layer not available'
    }
  }, [props.previewDatasetId])

  const chartContent = useMemo(() => (
    <ScatterPlotChart
      seriesX={seriesX}
      seriesY={seriesY}
      xAxisInterval={xAxisInterval as number}
      yAxisInterval={yAxisInterval as number}
      plotData={plotData}
      pointName={pointName}
      coordinates={memoCoords}
      downloadName={downloadName ?? ''}
      selectedDatasetInfo={selectedDatasetInfo}
      isLoading={updating}
      isOffline={isOffline}
      error={error || previewError}
      emptyText={emptyText}
    />
  ), [
    seriesX,
    seriesY,
    plotData,
    pointName,
    memoCoords,
    downloadName,
    selectedDatasetInfo,
    updating,
    error,
    previewError,
    yAxisInterval,
    xAxisInterval,
    emptyText
  ])

  const additionalContent = useMemo(() => (
    <ScatterPlotAdditionalInput
      xAxisInterval={xAxisInterval}
      yAxisInterval={yAxisInterval}
      month={month}
      onXAxisIntervalChange={doXAxisIntervalChange}
      onYAxisIntervalChange={doYAxisIntervalChange}
      onMonthChange={doMonthChange}
      categoryDataset={props.categoryDataset}
      categories={categories}
      datasetY={datasetY}
      onDatasetYChange={doDatasetYChange}
      variablesY={variablesY}
      variableY={variableY}
      onVariableChange={doVariableChange}
      dataPreformanceWarning={dataPreformanceWarning}
      isLoading={updating}
      hasError={!!previewError}
    />
  ), [
    categories,
    datasetY,
    doDatasetYChange,
    variablesY,
    variableY,
    doVariableChange,
    dataPreformanceWarning,
    updating,
    previewError,
    month,
    xAxisInterval,
    yAxisInterval
  ])

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={additionalContent}
      isLoading={updating}
    />
  )
}
